import { type FunctionComponent, useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { FormProvider, useForm } from 'react-hook-form'
import {
  Button,
  ButtonLink,
  Grid,
  GridItem,
  Image,
  Link,
  List,
  ListItem,
  TypographyV2 as Typography,
} from '@which/seatbelt'
import { dynamicDatalayerPush } from '@which/shared'

import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import classnames from 'classnames'

import type { FreeAccountMutation } from '../../../generated/frontend'
import type { Meta } from '../../../generated/frontend'
import { useFreeAccountMutation } from '../../../generated/frontend'
import { isLocal } from '../../../shared'
import { ErrorComponent } from '../../../shared/components/Error/ErrorComponent'
import { Loader } from '../../../shared/components/Loader'
import { robotsNoIndexMetaTag } from '../../../shared/hooks/fixtures/use-base-meta-tags'
import { useBaseMetaTags } from '../../../shared/hooks/useBaseMetaTags'
import { paywallUrls } from '../../../shared/paywall-urls'
import { decodeToUserJWT } from '../../../shared/types/UserJWTDecoded'
import { ErrorModal } from '../components/ErrorModal'
import { MarketingPreferences } from '../components/MarketingPreferences'
import { accountPagesDataLayer, setTransactionName } from '../utils'
import styles from './FreeAccountPage.module.scss'
import { freeAccountPageContent } from './FreeAccountPageData'
import WelcomePage from './WelcomePage'

type FreeAccountPageVariant = 'anonymous' | 'payingMember' | 'freeMember' | 'member'

interface FormValues {
  postConsent: 'Yes' | 'No'
  emailConsent: 'Yes' | 'No'
}

const FreeAccountPage: FunctionComponent = () => {
  const kinde = useKindeAuth()
  const [freeAccountMutation, { loading, error }] = useFreeAccountMutation()
  const [showModal, setShowModal] = useState(false)
  const [showWelcomePage, setShowWelcomePage] = useState(false)
  const [httpErrorCode, setHttpErrorCode] = useState('')
  const [paywallState] = useState(paywallUrls())
  const methods = useForm<FormValues>({ mode: 'onBlur' })
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods
  const [userState, setUserState] = useState<FreeAccountPageVariant>('anonymous')
  useEffect(() => {
    async function fetchClaim() {
      if (kinde && kinde.isAuthenticated) {
        const { permissions } = await kinde.getPermissions()
        if (
          permissions &&
          permissions.length > 0 &&
          permissions.some((p) => p.includes('reviews'))
        ) {
          setUserState('payingMember')
        } else {
          const freeAccountClaim = await kinde.getClaim('freeAccount')
          if (freeAccountClaim && freeAccountClaim.value) {
            setUserState('freeMember')
          } else {
            setUserState('member')
          }
        }
      } else {
        setUserState('anonymous')
      }
    }
    fetchClaim()
  }, [kinde])

  useEffect(() => {
    if (
      isSubmitting &&
      errors &&
      Object.keys(errors).length !== 0 &&
      Object.getPrototypeOf(errors) === Object.prototype
    ) {
      const fields = Object.keys(errors).join(' | ')
      dynamicDatalayerPush({
        eventCategory: 'FreeAccount',
        eventAction: 'Form Validation Errors',
        eventLabel: fields.toString(),
      })
      const elements = Object.keys(errors)
        .map((name) => document.getElementsByName(name)[0])
        .filter((el) => !!el)
      elements.sort((a, b) => b.scrollHeight - a.scrollHeight)
      elements[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [errors, isSubmitting])

  const handleOnCompleted = useCallback(
    () => (data?: FreeAccountMutation) => {
      if (!data?.freeAccount) {
        return
      }
      const { freeAccount } = data
      switch (freeAccount.__typename) {
        case 'FreeAccount':
          if (['200', '201'].includes(freeAccount.status)) {
            if (
              kinde &&
              kinde.isAuthenticated &&
              paywallState.kindeDomain &&
              paywallState.kindeClientId
            ) {
              kinde.refreshToken({
                domain: paywallState.kindeDomain,
                clientId: paywallState.kindeClientId,
                onRefresh: (data: { success: boolean; accessToken?: string }) => {
                  if (data.success && data.accessToken) {
                    const domain = isLocal() ? 'localhost' : '.which.co.uk'
                    const kindeAccessToken = data.accessToken
                    const decoded = decodeToUserJWT(kindeAccessToken)
                    const expireDate = new Date(decoded.exp * 1000).toUTCString()
                    document.cookie = `sessionId=${kindeAccessToken};path=/; domain=${domain};expires=${expireDate}`
                    document.cookie = `blaize_session=${kindeAccessToken};path=/; domain=${domain};expires=${expireDate}`

                    setShowWelcomePage(true)
                    setUserState('freeMember')
                  }
                },
              })
            }
          }
          break
        case 'FreeAccountError':
          setHttpErrorCode(freeAccount.status)
          setShowModal(true)
          break
      }
    },
    [kinde, paywallState.kindeClientId, paywallState.kindeDomain]
  )

  const handleOnSubmit = useCallback(
    async ({ postConsent, emailConsent }: FormValues) => {
      const sessionId = await kinde.getToken()

      freeAccountMutation({
        variables: {
          freeAccountRequest: {
            sessionId: sessionId || '',
            isPostOptIn: postConsent === 'Yes',
            isEmailOptIn: emailConsent === 'Yes',
          },
        },
        onCompleted: handleOnCompleted(),
      })
    },
    [freeAccountMutation, handleOnCompleted, kinde]
  )

  const { meta: metaTagObj, text: pageContent } = freeAccountPageContent[userState]
  const { getBaseMetaTags } = useBaseMetaTags()
  setTransactionName('free-account')

  const getMetaTags = ({
    meta,
    imageUrl,
    twitterCard,
  }: {
    meta: Partial<Meta>
    imageUrl: string
    twitterCard: string
  }) => getBaseMetaTags({ meta, imageUrl, twitterCard })

  const fullScreen = userState !== 'anonymous'
  if (error) {
    const { graphQLErrors } = error
    const extensionCode = graphQLErrors[0]?.extensions?.code as string
    if (['500', '400', '504', '422'].includes(extensionCode)) {
      setHttpErrorCode(extensionCode)
      setShowModal(true)
    } else {
      return <ErrorComponent error={error} />
    }
  }

  return (
    <div
      className={classnames(styles.freeAccountPage, fullScreen && styles.freeAccountPageFullHeight)}
    >
      <Helmet>
        {getMetaTags({
          meta: metaTagObj,
          imageUrl: metaTagObj.canonical,
          twitterCard: 'summary_large_image',
        })}
        <script>{accountPagesDataLayer({ content_type: 'Free account signup' })}</script>
        {robotsNoIndexMetaTag}
      </Helmet>
      {kinde && kinde.isLoading ? (
        <Loader />
      ) : showWelcomePage ? (
        <WelcomePage />
      ) : (
        <>
          <ErrorModal
            httpErrorCode={httpErrorCode}
            showModal={showModal}
            setShowModal={setShowModal}
          />
          <div
            className={classnames(
              styles.freeAccountPageHeroBanner,
              fullScreen && styles.freeAccountPageHeroBannerFullHeight
            )}
          >
            <div
              className={classnames(
                styles.freeAccountPageHeroBannerBackground,
                fullScreen && styles.freeAccountPageHeroBannerBackgroundFullHeight
              )}
            >
              <div className={classnames(!fullScreen && styles.freeAccountPageHeroBannerLine)} />
            </div>
            <Grid className={styles.freeAccountPageHeroBannerContent}>
              <GridItem
                span={{
                  medium: userState === 'member' ? 5 : 6,
                  large: userState === 'member' ? 5 : 6,
                }}
                columnStart={{ medium: 1, large: 1 }}
              >
                <div
                  className={classnames(
                    styles.freeAccountPageHeroBannerSignup,
                    (userState === 'member' || userState === 'anonymous') &&
                      styles.freeAccountPageHeroBannerSignupMember
                  )}
                >
                  <Typography
                    className={styles.freeAccountPageHeroBannerTitle}
                    tag="h1"
                    textStyle="sb-text-heading-page-title"
                  >
                    {pageContent.heroTitle}
                  </Typography>
                  <Typography
                    tag="p"
                    textStyle="sb-text-heading-standfirst"
                    dangerouslySetInnerHTML={{ __html: pageContent.heroDescription }}
                  />
                  <div className={styles.freeAccountPageHeroBannerLinks}>
                    {userState === 'anonymous' && (
                      <Link href={pageContent.sampleLink} textStyle="sb-text-body-default-strong">
                        {pageContent.heroSampleLink}
                      </Link>
                    )}
                    {userState === 'member' ? (
                      <FormProvider {...methods}>
                        <form method="post">
                          <MarketingPreferences />
                          <Button
                            className={styles.freeAccountPageHeroBannerButton}
                            data-testid="free-account-button"
                            enableSpinner={loading}
                            onClick={handleSubmit(handleOnSubmit)}
                          >
                            {pageContent.heroSignupButton}
                          </Button>
                        </form>
                      </FormProvider>
                    ) : (
                      <ButtonLink
                        className={styles.freeAccountPageHeroBannerButton}
                        href={pageContent.signupLink}
                      >
                        {pageContent.heroSignupButton}
                      </ButtonLink>
                    )}
                  </div>
                </div>
              </GridItem>
              <GridItem span={{ medium: 6, large: 6 }} columnStart={{ medium: 7, large: 7 }}>
                <Image
                  alt={pageContent.heroImageAlt}
                  src={pageContent.heroImage}
                  className={classnames(
                    styles.freeAccountPageHeroBannerImageOverflow,
                    userState === 'member' && styles.freeAccountPageHeroBannerImageMember
                  )}
                />
              </GridItem>
            </Grid>
          </div>
          {!fullScreen && (
            <Grid className={styles.freeAccountPageDetails}>
              <GridItem span={{ medium: 4, large: 5 }} columnStart={{ medium: 2, large: 1 }}>
                <Typography
                  tag="h2"
                  textStyle="sb-text-heading-medium"
                  className={styles.freeAccountPageDetailsTitle}
                >
                  {pageContent.howItWorksTitle}
                </Typography>
                <List as="ul" className={styles.freeAccountPageList}>
                  {pageContent.bulletPoints.map((point, idx) => (
                    <ListItem key={idx} className={styles.freeAccountPageListItem}>
                      <Typography tag="span" textStyle="sb-text-body-default-regular">
                        {point}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </GridItem>
              <GridItem span={{ medium: 5, large: 6 }} columnStart={{ medium: 7, large: 7 }}>
                <div className={styles.freeAccountPageAboutSection}>
                  <Typography tag="h2" textStyle="sb-text-heading-medium">
                    {pageContent.aboutTitle}
                  </Typography>
                  {pageContent.aboutParagraphs.map((p, i) => (
                    <Typography key={i} tag="p" textStyle="sb-text-body-default-regular">
                      {p}
                    </Typography>
                  ))}
                </div>
              </GridItem>
            </Grid>
          )}
        </>
      )}
    </div>
  )
}

export default FreeAccountPage
